import { Navigate } from "react-router-dom";
import type { ReactNode } from "react";
import { useAppSelector } from "@/app/hooks";
import { selectIsAuthenticated, selectUser } from "@/features/auth/authSlice";
import { useUserRole } from "@/hooks/useUserRole";

interface AccessibleMenuGuardProps {
  children: ReactNode;
  /** `user_access` / accessibleMenu key (e.g. `underwriting_config`). */
  feature: string;
  redirectTo?: string;
}

/**
 * Access when `accessibleMenu[feature]` is truthy (e.g. empty array for underwriting_config).
 * Mirrors Angular sidebar *ngIf="accessibleMenu?.underwriting_config" style checks.
 */
export function AccessibleMenuGuard({
  children,
  feature,
  redirectTo = "/dashboard",
}: AccessibleMenuGuardProps) {
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const user = useAppSelector(selectUser);
  const { hasAccess } = useUserRole();

  if (!isAuthenticated) {
    return <>{children}</>;
  }
  if (!user || !user.current_role) {
    return <>{children}</>;
  }

  if (!hasAccess(feature)) {
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
}
